export const getBaseUrl = () => {
  return import.meta.env.VITE_APP_URL || window.location.origin;
};

export const getBasename = () => {
  return import.meta.env.VITE_ROUTER_BASENAME || '/';
};

const buildUrl = (base: string, path: string) => {
  const baseUrl = base.replace(/\/+$/, '');
  const basename = getBasename().replace(/^\/+|\/+$/g, '');
  const cleanPath = path.replace(/^\/+/, '');

  // Check if baseUrl already ends with basename
  if (baseUrl.endsWith(basename)) {
    return `${baseUrl}/${cleanPath}`;
  }

  return basename
    ? `${baseUrl}/${basename}/${cleanPath}`
    : `${baseUrl}/${cleanPath}`;
};

export const getFullUrl = (path: string) => buildUrl(getBaseUrl(), path);

/**
 * Auth redirects must return to the origin the user is actually on, never to a
 * canonical domain from build config.
 *
 * VITE_APP_URL pointed at the marketing site in production, so Supabase got a
 * redirect_to it doesn't allow, discarded it and dropped every Google sign-in
 * on the Site URL — /auth/callback never ran. Reading the live origin also
 * makes preview deployments work without per-environment configuration.
 */
const getAuthRedirectUrl = (path: string) => buildUrl(window.location.origin, path);

export const getResetPasswordUrl = () => {
  return getAuthRedirectUrl('/reset-password');
};

export const getAuthCallbackUrl = () => {
  return getAuthRedirectUrl('/auth/callback');
};

/**
 * Get the base URL for Supabase Edge Functions
 * Uses VITE_SUPABASE_URL from environment variables
 * This is the same URL used for the Supabase client, ensuring consistency
 */
export const getSupabaseFunctionsUrl = (): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  
  if (!supabaseUrl) {
    console.warn('⚠️ VITE_SUPABASE_URL not defined, defaulting to localhost');
    return 'http://127.0.0.1:54321';
  }
  
  return supabaseUrl;
};

/**
 * Get the full URL for a specific Supabase Edge Function
 * @param functionName - The name of the edge function (e.g., 'get_training_instructor')
 * @returns The full URL to call the edge function
 */
export const getEdgeFunctionUrl = (functionName: string): string => {
  const baseUrl = getSupabaseFunctionsUrl();
  return `${baseUrl}/functions/v1/${functionName}`;
}; 